import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Svg from 'rp-react-svg-icon';
import RpTable from 'rp-react-table';
import browserHistory from '../../../index';
import columns from './columns';
import getSortFn from './getSortFn';
import SpecialRow from './SpecialRow';
import Slider from './Slider';
import mergeWithMR from './mergeWithMR';
import { formType, myRatingsType } from '../dataPropTypes';
import { isRunner } from './helper';
import ContentBarrier from '../../../shared/contentBarrier/ContentBarrier';
import analyticsPage from '../../../analyticsPage';

const sliderConfig = {
    nonRunnerSwitcher: { stateProp: 'isNRShown', title: 'Non-Runner Info' },
    delimiterLineSwitcher: { stateProp: 'isBreakShown', title: 'Breaks (50+ days)' },
    myRatingSwitcher: { title: 'My Rating' },
};

class FormTable extends Component {
    constructor(props) {
        super(props);

        const isMyRatingShown = localStorage.getItem('myRatingSwitcher') === 'true';
        const isDelimiterLineShown = localStorage.getItem('delimiterLineSwitcher') === 'true';

        this.state = {
            isNRShown: localStorage.getItem('nonRunnerSwitcher') === 'true',
            isBreakShown: isDelimiterLineShown,
            sortBy: '',
            indexDefault: 0,
        };

        this.getColumnsConfig = columns.bind(null, this.updateSortBy);
        this.handleSlider = this.handleSlider.bind(this);
        this.handleMyRatingSlider = this.handleMyRatingSlider.bind(this);
        this.raceRowsLimit = 5;
        this.shouldShowContentBarrier = false;

        if (isMyRatingShown) {
            this.myRatingToggle(true);
        } else {
            this.myRatingToggle();
        }
    }

    componentDidMount = () => {
        const { data, hasAccess } = this.props;
        this.shouldShowContentBarrier = !hasAccess && data.form.filter(row => isRunner(row) && !row.isSpecial).length > this.raceRowsLimit;
        analyticsPage(data, this.shouldShowContentBarrier);
    };

    onSortIndexChange = (index) => {
        this.setState({ indexDefault: index });
    }

    getFilteredData = () => {
        const { data: { form }, params } = this.props;
        const { isNRShown, isMRShown } = this.state;

        const sortedForm = form.sort((a, b) => new Date(b.raceDatetime) - new Date(a.raceDatetime));

        const tableData = [];
        let realRaceCount = 0;
        let allowOnlyNonRealAfterLimit = false;

        for (const row of sortedForm) {
            const isSpecialRow = row.isSpecial;
            const isRunnerRow = isRunner(row) && !isSpecialRow;
            const isNonRunnerRow = row.raceOutcomeCode === 'NR';


            if (!isNRShown && isNonRunnerRow) {
                continue;
            }

            if (isRunnerRow) {
                if (realRaceCount < this.raceRowsLimit) {
                    tableData.push(row);
                    realRaceCount += 1;
                } else {
                    allowOnlyNonRealAfterLimit = true;
                }
            } else {
                tableData.push(row);
            }

            if (allowOnlyNonRealAfterLimit && isRunnerRow) {
                break;
            }
        }

        return isMRShown ? mergeWithMR(tableData, params.myRatings) : tableData;
    }

    handleSlider(key) {
        const isCurrentlyEnabled = localStorage.getItem(key) === 'true';
        this.setState({ [sliderConfig[key].stateProp]: !isCurrentlyEnabled });
        localStorage.setItem(key, !isCurrentlyEnabled);
    }

    handleMyRatingSlider() {
        const { params: { mr: myRatings } } = this.props;
        const key = 'myRatingSwitcher';
        const show = !myRatings.isShown;

        localStorage.setItem(key, show);
        this.myRatingToggle(show);
    }

    myRatingToggle = (show = false) => {
        const { params: { id, name, mr: myRatings } } = this.props;
        const myRatingState = show ? 'show' : 'hide';
        const configUrl = {
            show: `horse/${id}/${name}/form/my-ratings`,
            hide: `horse/${id}/${name}/form`,
        };

        browserHistory.replace(configUrl[myRatingState]);
        if (myRatings[myRatingState]) {
            myRatings[myRatingState]();
        }
    }

    updateSortBy = (sortBy) => {
        if (sortBy !== this.state.sortBy) {
            this.setState({ sortBy });
        }
    }

    render() {
        const {
            data,
            selectedFilter,
            hasSpecial,
            params,
            hasAccess,
        } = this.props;

        const { form, loading, myRatings } = data;
        const { mr: { isShown: isMRShown } } = params;
        const { isNRShown, isBreakShown, sortBy, indexDefault } = this.state;

        let tableData = isNRShown ? form : form.filter(isRunner);

        if (hasAccess) {
            tableData = isMRShown ? mergeWithMR(tableData, myRatings) : tableData;
        } else {
            tableData = this.getFilteredData();
        }

        return (
            <section>
                <div className="hp-formTable__header">
                    <h2 className="hp-formTable__title">Form {selectedFilter && `- ${selectedFilter}`}</h2>
                    <div className="hp-formTable__sliderContainer">
                        {Object.keys(sliderConfig).map(key => (
                            <Slider
                                key={key}
                                handleSlider={() => (
                                    key === 'myRatingSwitcher'
                                        ? this.handleMyRatingSlider()
                                        : this.handleSlider(key)
                                )}
                                sliderState={
                                    key === 'myRatingSwitcher' ? isMRShown : this.state[sliderConfig[key].stateProp]
                                }
                                title={sliderConfig[key].title}
                                id={key}
                                disabled={
                                    key === 'delimiterLineSwitcher' && (selectedFilter || indexDefault !== 0)
                                }
                            />
                        ))}
                    </div>
                    {loading && (
                        <Svg
                            iconName="spinner"
                            size="Large"
                            className="ui-animation_spinnerAnimation"
                        />
                    )}
                </div>
                <RpTable
                    className={classNames(
                        'hp-formTable',
                        isMRShown && 'hp-formTable__my-ratings',
                        isNRShown && 'nonRunnerSliderSwitcher',
                        !isBreakShown && 'hp-formTable__delimiterHide'
                    )}
                    data={tableData}
                    columns={this.getColumnsConfig(sortBy)}
                    noDataText="No form for this horse"
                    sortable={hasAccess}
                    initialSortIdx={indexDefault}
                    filter={getSortFn(selectedFilter)}
                    specialRow={hasSpecial ? SpecialRow : undefined}
                    onSortUpdate={this.onSortIndexChange}
                />
                {this.shouldShowContentBarrier && <ContentBarrier />}
            </section>
        );
    }
}

FormTable.propTypes = {
    data: PropTypes.shape({
        form: PropTypes.arrayOf(formType),
        myRating: myRatingsType,
        loading: PropTypes.bool,
    }),
    params: PropTypes.shape({ mr: PropTypes.shape({ isShown: PropTypes.bool }) }),
    selectedFilter: PropTypes.string,
    hasSpecial: PropTypes.bool.isRequired,
    hasAccess: PropTypes.bool.isRequired,
};

export default FormTable;



// WEBPACK FOOTER //
// ./src/js/horse/form/formTable/FormTable.jsx